import { SalesChannelType } from '../../core/sales-channel.type';
import { PriceValue, Timestamp } from '../../core/utils';
import { LooseAutocomplete, SalesChannel, TODO } from '../../core';
import { FormCategoryType, FormDisplaySettings, FormState } from '../../forms';


// ==================== Enum Types ====================
type EventStateEnum = 'deleted' | 'active' | 'cancelled' | 'suspended';

type AdmissionTypeEnum = 'standard' | 'generalAdmission';

type EventTemplateTypeEnum = 'standard' | 'private';

type PurchaseTimeFrameEnum = 'after' | 'before' | 'when';

type FlexPayTypeEnum = 'payWhatYouWish' | 'discount';

type ResourceCategoryEnum = 'Personnel' | 'Facilities' | 'Equipment' | 'Other';

type EventResourceRequestTypeEnum = 'scheduleBased' | 'instanceBased';

type FormTypeEnum = 'order' | 'ticket' | 'attendee' | 'request';

type FormSourceEnum = 'acme' | 'google';

type DocumentStateEnum = 'draft' | 'published';

type FormCategoryTypeEnum = 'event' | 'membership';

type FormDisplaySettingsEnum = 'perCart' | 'perItem' | 'perCard' | 'perTicket';

type FormPropertiesEnum = 'hidden' | 'displayed' | 'required';

type PrinterTypeEnum = 'ticketPrinter' | 'receiptPrinter';

type PrinterSelectionEnum = 'theme' | 'custom' | 'default';

type TicketConfigStatus = 'active' | 'inactive';

type ResourceFulfillmentStatusEnum = 'fulfilled' | 'partial' | 'unfulfilled' | 'none';

type AddOnTypeEnum = 'inventory' | 'donation';

type StartEndEnum = 'starts' | 'ends';

type TimeFrameWhenEnum = 'before' | 'after' | 'when' | 'duration';

type EventScheduleInstanceTypeEnum = 'event' | 'schedule';

// ==================== Nested Interfaces ====================

// ---------- Check-In & Purchase Configuration ----------

interface CheckInWindowVo {
  minutesBefore?: number;
  minutesAfter?: number;
  blockScan?: boolean;
  autoRebook?: boolean;
}

interface PostStartPurchaseVo {
  allowLateTicketPurchase?: boolean;
  minutesAfter?: number;
}

interface PurchaseAvailabilityVo {
  timeFrame?: PurchaseTimeFrameEnum;
  minutes?: number;
}

export interface EventTicketDetailsVo {
  ticketDurationDays?: number;
  checkInWindow?: CheckInWindowVo;
  postStartPurchase?: PostStartPurchaseVo;
  purchaseAvailability?: PurchaseAvailabilityVo;
  ticketPrintLines?: string[];
  hideEventDate?: boolean;
  doNotPrint?: boolean;
  expireFromEventDate?: boolean;
}

// ---------- Pricing ----------

export interface PersonTypeVo {
  id?: string;
  name?: string;
  description?: string;
  minAge?: number;
  maxAge?: number;
  minQuantity?: number;
  maxQuantity?: number;
  displayOrder?: number;
}

export interface SalesChannelEntryVo {
  name?: string;
  channel?: string;
  enabled?: boolean;
}

interface EventPriceListEntryVo {
  personType?: PersonTypeVo;
  price?: PriceValue;
  basePrice?: PriceValue;
  discountedPrice: PriceValue;
  discountedBasePrice?: PriceValue;
  salesChannels?: SalesChannelEntryVo[];
  displayOrder?: number;
  discountId?: string;
  discountName?: string;
}

interface EventPriceListVo {
  priceListId?: string;
  prices?: EventPriceListEntryVo[];
}

// ---------- Resources ----------

interface NameValuePairVo {
  name?: string;
  value?: string;
}

interface EventResourceVo {
  resourceType?: string;
  resourceTemplateId?: string;
  resourceTemplateName?: string;
  resourceId?: string;
  resourceName?: string;
  fields?: NameValuePairVo[];
  quantity?: number;
  resourceCategory?: ResourceCategoryEnum;
  values?: string[];
  resourceStartTime?: Timestamp;
  resourceEndTime?: Timestamp;
  eventScheduleResourceRequestId?: string;
  notes?: string;
  eventResourceId?: string;
  eventResourceRequestId?: string;
  eventResourceRequestType?: EventResourceRequestTypeEnum;
  eventInstanceResourceRequestId?: string;
}

interface FieldDataVo {
  name?: string;
  value?: string;
  type?: string;
}

interface ResourceUserVo {
  userId?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface ResourceCapacityVo {
  capacity?: number;
  used?: number;
}

interface ResourceVo {
  id?: string;
  resourceTemplateId?: string;
  resourceTemplateName?: string;
  name?: string;
  description?: string;
  ownerId?: string;
  createdOn?: string;
  createdBy?: string;
  updatedOn?: string;
  updatedBy?: string;
  fields?: FieldDataVo[];
  resourceCategory?: ResourceCategoryEnum;
  resourceUser?: ResourceUserVo;
  resourceCapacity?: ResourceCapacityVo;
}

interface ResourceTimeFrameVo {
  when?: TimeFrameWhenEnum;
  minutes?: number;
  instanceType?: EventScheduleInstanceTypeEnum;
  startEnd?: StartEndEnum;
}

interface EventScheduleResourceTimeFrameVo {
  start?: ResourceTimeFrameVo;
  end?: ResourceTimeFrameVo;
}

interface EventScheduleResourceVo {
  resourceType?: string;
  resourceTemplateId?: string;
  resourceId?: string;
  resourceName?: string;
  quantity?: number;
  resourceCategory?: ResourceCategoryEnum;
  values?: string[];
}

interface EventScheduleResourceRequestVo {
  id?: string;
  resourceTimeFrame?: EventScheduleResourceTimeFrameVo;
  eventResource?: EventScheduleResourceVo;
  notes?: string;
  eventResourceRequestType?: EventResourceRequestTypeEnum;
  sendEmail?: boolean;
}

interface EventResourceRequestSummaryVo {
  totalRequests?: number;
  fulfilledRequests?: number;
}

interface EventResourceSummaryVo {
  people?: EventResourceRequestSummaryVo;
  place?: EventResourceRequestSummaryVo;
  equipment?: EventResourceRequestSummaryVo;
  totalRequestsCount?: number;
  fulfilledRequestsCount?: number;
  resourceFulfillmentStatus?: ResourceFulfillmentStatusEnum;
  hasResourceRequests?: boolean;
}

// ---------- Forms ----------

interface FormPreferencesVo {
  pos?: FormPropertiesEnum;
  b2c?: FormPropertiesEnum;
}

export interface FormVo {
  id?: string;
  name?: string;
  description?: string;
  itemType?: string;
  type?: FormTypeEnum;
  source?: FormSourceEnum;
  sourceFormId?: string;
  sourceFormUrl?: string;
  sourceFormEditUrl?: string;
  sourceFormThumbnail?: string;
  sourceFormDefinition?: string;
  state?: DocumentStateEnum;
  draftId?: string;
  lastUpdatedDate?: Timestamp;
  lastPublishedDate?: Timestamp;
  preferences?: FormPreferencesVo;
  oncePerCart?: boolean;
  formCategoryType?: FormCategoryTypeEnum;
  displayOrder?: number;
  formDisplaySettings?: FormDisplaySettingsEnum;
}

// ---------- Ticket Config ----------

interface TicketConfigPrinterVo {
  printerId?: string;
  name?: string;
  config?: string;
  printerType?: PrinterTypeEnum;
  selection?: PrinterSelectionEnum;
}

interface TicketConfigVo {
  id?: string;
  name?: string;
  description?: string;
  printerConfigs?: TicketConfigPrinterVo[];
  assigned?: boolean;
  status?: TicketConfigStatus;
}

// ---------- Field Status ----------

interface EventFieldStatusEntryVo {
  modified?: boolean;
  locked?: boolean;
}

interface EventFieldStatusVo {
  name?: EventFieldStatusEntryVo;
  description?: EventFieldStatusEntryVo;
  emailConfirmationMessage?: EventFieldStatusEntryVo;
  capacity?: EventFieldStatusEntryVo;
  startTime?: EventFieldStatusEntryVo;
  endTime?: EventFieldStatusEntryVo;
  priceList?: EventFieldStatusEntryVo;
  resources?: EventFieldStatusEntryVo;
  ticketDetails?: EventFieldStatusEntryVo;
  images?: EventFieldStatusEntryVo;
  shortDescription?: EventFieldStatusEntryVo;
  flexPays?: EventFieldStatusEntryVo;
  emailCancellationMessage?: EventFieldStatusEntryVo;
  ticketConfig?: EventFieldStatusEntryVo;
  orderForms?: EventFieldStatusEntryVo;
  customFields?: EventFieldStatusEntryVo;
  ticketDurationDays?: EventFieldStatusEntryVo;
  salesChannels?: EventFieldStatusEntryVo;
}

// ---------- FlexPay ----------

interface FlexPayEventVo {
  flexPayId?: string;
  name?: string;
  type?: FlexPayTypeEnum;
  startTime?: Timestamp;
  endTime?: Timestamp;
  discountId?: string;
}

interface EventOriginalFieldsVo {
  startTime?: Timestamp;
  endTime?: Timestamp;
  flexPays?: FlexPayEventVo[];
}

// ---------- Add-Ons ----------

interface AddOnVo {
  id?: number;
  tenantId?: number;
  resourceId?: number;
  name?: string;
  description?: string;
  sku?: string;
  price?: string; // Currency serialized as string
  basePrice?: string; // Currency serialized as string
  quantity?: number;
  type?: AddOnTypeEnum;
  displayOrder?: number;
  active?: boolean;
  printTicket?: boolean;
  createdOn?: Timestamp;
  updatedOn?: Timestamp;
  salesChannels?: SalesChannelEntryVo[];
}

interface AddOnListVo {
  inventoryIds?: number[];
  inventoryAddOns?: AddOnVo[];
}

// ---------- Custom Fields ----------

interface CustomFieldVo {
  name?: string;
  value?: string;
}

// ---------- Color Category ----------

interface ColorCategoryVo {
  backgroundColor?: string;
  textColor?: string;
  categoryName?: string;
}

// ---------- Organization ----------

interface EventOrganizationVo {
  id?: number;
  name?: string;
}

// ---------- Images ----------

interface ImageVo {
  id?: string;
  description?: string;
  primary?: boolean;
  thumbnail?: string;
  highRes?: string;
  screen?: string;
  preview?: string;
}

// ==================== Main Interface ====================

export interface EventVo {
  id?: string;
  templateId?: string;
  name?: string;
  description?: string;
  shortDescription?: string;
  emailConfirmationMessage?: string;
  emailCancellationMessage?: string;

  capacity?: number;
  startTime?: Timestamp;
  endTime?: Timestamp;
  formattedStartTime?: string;

  resourceIds?: string[];
  resources?: EventResourceVo[];
  resourceDetails?: ResourceVo[];
  resourceRequests?: EventScheduleResourceRequestVo[];
  eventResourceSummary?: EventResourceSummaryVo;

  priceList?: EventPriceListVo;

  salesRestrictions?: SalesChannelType[];
  salesChannels?: SalesChannelEntryVo[];

  scheduleId?: string;
  ticketDetails?: EventTicketDetailsVo;
  ticketConfig?: TicketConfigVo;

  manuallyDeleted?: boolean;
  offSchedule?: boolean;

  fieldStatus?: EventFieldStatusVo;
  originalFields?: EventOriginalFieldsVo;

  flexPays?: FlexPayEventVo[];

  state?: EventStateEnum;
  cancellationDate?: Timestamp;

  orderForms?: FormVo[];
  customFields?: CustomFieldVo[];

  colorCategory?: ColorCategoryVo;
  organization?: EventOrganizationVo;

  images?: ImageVo[];

  addOns?: AddOnListVo;
  type?: EventTemplateTypeEnum;
  admissionType?: AdmissionTypeEnum;

  soldQuantity?: number;
  checkInCount?: number;
  available?: boolean;

  lastModified?: Timestamp;

  orderRequestId?: string;
}

/**
 * Person type for ticket pricing
 */
export interface PersonType {
  id: string;
  name: string;
  description: string;
  active: boolean;
  type: LooseAutocomplete<'Individual' | 'Group'>;
  displayOrder: number;
}

/**
 * Price entry in an event's price list
 */
export interface EventPrice {
  personType: PersonType;
  price: string;
  salesChannels: SalesChannel[];
  displayOrder: number;
  discountedPrice: string;
  discount: string;
  csrDisabled: number;
  unitPrice: string;
  retailUnitPrice: string;
  eventId: string;
  ticketingTypeId: string;
  ticketingTypeName: string;
  name: string;
  ticketingTypeDescription: string;
  quantity: number;
  resolvedQuantity: number;
  isRetail: boolean;
  itemType: 'Event';
}

/**
 * Price list for an event
 */
export interface EventPriceList {
  prices: EventPrice[];
  priceListId: string;
}

/**
 * Check-in window configuration
 */
export interface CheckInWindow {
  minutesBefore: number;
  minutesAfter: number;
  blockScan: boolean;
  autoRebook: boolean;
}

/**
 * Post-start purchase settings
 */
export interface PostStartPurchase {
  allowLateTicketPurchase: boolean;
}

/**
 * Purchase availability settings
 */
export interface PurchaseAvailability {
  timeFrame: LooseAutocomplete<'after' | 'before'>;
  minutes: number;
}

/**
 * Ticket details configuration for an event
 */
export interface EventTicketDetails {
  ticketDurationDays: number;
  checkInWindow: CheckInWindow;
  postStartPurchase: PostStartPurchase;
  purchaseAvailability: PurchaseAvailability;
  ticketPrintLines: string[];
  doNotPrint: boolean;
  expireFromEventDate: boolean;
  hideEventDate: boolean;
}

/**
 * Order form attached to an event
 */
export interface EventOrderForm {
  id: string;
  name: string;
  type: 'order';
  source: 'acme';
  sourceFormId: string;
  sourceFormUrl: string;
  sourceFormEditUrl: string;
  sourceFormDefinition: string;
  state: FormState;
  draftId: string;
  lastUpdatedDate: Timestamp;
  lastPublishedDate: Timestamp;
  preferences: {
    pos: LooseAutocomplete<'displayed' | 'required' | 'hidden'>;
    b2c: LooseAutocomplete<'displayed' | 'required' | 'hidden'>;
    kiosk: LooseAutocomplete<'displayed' | 'required' | 'hidden'>;
    insideSales: LooseAutocomplete<'displayed' | 'required' | 'hidden'>;
  };
  oncePerCart: boolean;
  formCategoryType: FormCategoryType;
  displayOrder: number;
  formDisplaySettings: FormDisplaySettings;
}

/**
 * EventOrderForm with additional cart context (event name, item type)
 */
export interface CartEventOrderForm extends EventOrderForm {
  uuid?: string;
  isRequired?: boolean;
  event: {
    id: string;
    name: string;
    displayStartDate: string;
    displayStartTime: string;
    displayEndTime: string;
    startTime: Timestamp;
  };
  itemType: 'event';
}

/**
 * Type guard to identify a cart event order form by its literal `itemType`.
 */
export function isCartEventOrderForm(form: object): form is CartEventOrderForm {
  return 'itemType' in form && (form as { itemType: unknown }).itemType === 'event';
}


/**
 * Custom field on an event
 */
export interface EventCustomField {
  name: string;
  value: string;
}

/**
 * Color category for event display
 */
export interface EventColorCategory {
  backgroundColor: string;
  textColor: string;
}

/**
 * Inventory add-on item for an event
 */
export interface EventInventoryAddOn {
  id: number;
  tenantId: number;
  name: string;
  description: string;
  sku: string;
  price: string;
  quantity: number;
  type: LooseAutocomplete<'doNotTrack' | 'tracked'>;
  displayOrder: number;
  active: boolean;
  createdOn: Timestamp;
  updatedOn: Timestamp;
  salesChannels: SalesChannel[];
  printTicket: boolean;
  eventId: string;
  inventoryId: number;
  inventoryName: string;
  unitPrice: string;
  amount: string;
  retailUnitPrice: string;
  resolvedQuantity: number;
  itemType: 'Inventory';
  csrDisabled?: boolean;
}

/**
 * Add-ons configuration for an event
 */
export interface EventAddOns {
  inventoryIds: number[];
  inventoryAddOns: EventInventoryAddOn[];
}

/**
 * Membership level in member restriction
 */
export interface EventMembershipLevel {
  auxiliary: boolean;
  id: string;
  name: string;
  membershipLevelGroup?: string;
}

/**
 * Member restriction configuration for an event (processed/computed version)
 */
export interface EventMemberRestriction {
  memberOnlyEvent: boolean;
  restricted: boolean;
  membershipLevels: EventMembershipLevel[];
}

/**
 * Full event detail object (populated in CartEvent.event after API fetch)
 */
export interface EventDetail {
  id: string;
  name: string;
  description: string;
  templateId: string;
  emailConfirmationMessage: string;
  emailCancellationMessage: string;
  capacity: number;
  startTime: Timestamp;
  endTime: Timestamp;
  priceList: EventPriceList;
  resourceDetails: TODO[];
  scheduleId: string;
  salesRestrictions: TODO[];
  ticketDetails: EventTicketDetails;
  manuallyDeleted: boolean;
  fieldStatus: Record<string, TODO>;
  originalFields: {
    startTime: Timestamp;
    endTime: Timestamp;
  };
  state: LooseAutocomplete<'active' | 'cancelled' | 'suspended'>;
  ticketConfig: Record<string, TODO>;
  orderForms: EventOrderForm[];
  customFields: EventCustomField[];
  offSchedule: boolean;
  resourceRequests: TODO[];
  colorCategory: EventColorCategory;
  images: TODO[];
  formattedStartTime: string;
  soldQuantity: number;
  salesChannels: SalesChannel[];
  addOns: EventAddOns;
  type: LooseAutocomplete<'standard' | 'combo'>;
  checkInCount: number;
  available: boolean;
  admissionType: LooseAutocomplete<'generalAdmission' | 'timedEntry'>;
  lastModified: Timestamp;
  lastPublishedBy: string;
  lastPublished: Timestamp;
  /** Formatted start date (e.g., "3/2/26") - computed dynamically */
  startDate: string;
  /** Formatted start hour (e.g., "8:00 AM") - computed dynamically */
  startHour: string;
  /** Additional prices beyond initial display limit */
  morePrices: EventPrice[];
  /** Additional add-ons beyond initial display limit */
  moreAddOns: EventInventoryAddOn[];
  /** Member restriction rules for this event */
  memberRestriction: EventMemberRestriction | null;
}

/**
 * Represents an event time slot used for switching events in the cart.
 * Contains the minimum required properties from EventDetail.
 */
export interface EventTimeSlot {
  id: string;
  name?: string;
  startTime?: Timestamp;
  availableTickets?: number;
  priceList: EventPriceList;
  addOns?: EventAddOns;
}

/**
 * Ticket-like object used for finding and comparing tickets
 * Used in CartEventsService.findTicketType method
 */
export interface TicketLike {
  ticketingTypeId: string;
  isRetail?: boolean;
  quantity: number;
  price?: string;
  discountedPrice?: string;
  unitPrice?: string;
  retailUnitPrice?: string;
}

/**
 * Result type for ticket search operations
 * Contains the index and ticket data when a matching ticket is found
 */
export interface FoundTicketType {
  index: number;
  ticket: TicketLike;
}

